import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import styles from './Toast.module.css';

export type ToastKind = 'success' | 'error' | 'info';
export type ToastPlacement = 'default' | 'center';

interface ToastEntry {
  id: number;
  kind: ToastKind;
  message: string;
  placement: ToastPlacement;
}

interface ToastContextValue {
  push: (message: string, kind?: ToastKind, durationMs?: number, placement?: ToastPlacement) => void;
  success: (message: string, durationMs?: number) => void;
  error: (message: string, durationMs?: number) => void;
  info: (message: string, durationMs?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 3200;

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const counterRef = useRef(0);

  const push = useCallback((message: string, kind: ToastKind = 'info', durationMs: number = DEFAULT_DURATION_MS, placement: ToastPlacement = 'default') => {
    counterRef.current += 1;
    const id = counterRef.current;
    setToasts((prev) => [...prev, { id, kind, message, placement }]);
    if (durationMs > 0) {
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, durationMs);
    }
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (m, d) => push(m, 'success', d),
      error: (m, d) => push(m, 'error', d),
      info: (m, d) => push(m, 'info', d),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRack toasts={toasts.filter(toast => toast.placement === 'default')} />
      <ToastRack toasts={toasts.filter(toast => toast.placement === 'center')} centered />
    </ToastContext.Provider>
  );
}

function ToastRack({ toasts, centered = false }: { toasts: ToastEntry[]; centered?: boolean }) {
  if (toasts.length === 0) return null;
  return (
    <div className={`${styles.rack} ${centered ? styles.rackCenter : ''}`} role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={styles.toast}>
          <span className={[styles.kindBadge, kindClass(t.kind)].join(' ')}>{kindGlyph(t.kind)}</span>
          <span className={styles.message}>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

function kindClass(kind: ToastKind): string {
  if (kind === 'success') return styles.kindSuccess;
  if (kind === 'error') return styles.kindError;
  return styles.kindInfo;
}

function kindGlyph(kind: ToastKind) {
  if (kind === 'success') {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  }
  if (kind === 'error') {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    );
  }
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}
